import React, { useState } from 'react';
import styled from 'styled-components';
import { Button, Spinner, COLOR_SYSTEM, Text } from 'components/atoms';
import { GroundTruthFilters as Filters } from 'services/api/aiReviewTest/groundTruthApi';
import { GroundTruth } from 'common/models/types';
import GroundTruthRow from './GroundTruthRow';
import GroundTruthFilters from './GroundTruthFilters';

interface GroundTruthListSectionProps {
  groundTruths: (GroundTruth & { patient?: any })[];
  isLoading: boolean;
  filters: Filters;
  onFiltersChange: (filters: Filters) => void;
  onCreateClick: () => void;
  onEditClick: (groundTruth: GroundTruth) => void;
  onDeleteClick: (id: number) => void;
  onExperimentClick: () => void;
}

const GroundTruthListSection: React.FC<GroundTruthListSectionProps> = ({
  groundTruths,
  isLoading,
  filters,
  onFiltersChange,
  onCreateClick,
  onEditClick,
  onDeleteClick,
  onExperimentClick,
}) => {
  const handleResetFilters = () => {
    onFiltersChange({});
  };

  if (isLoading) {
    return (
      <LoadingContainer>
        <Spinner size="Large" />
      </LoadingContainer>
    );
  }

  return (
    <Container>
      <Header>
        <Title>Ground Truth 관리</Title>
        <HeaderActions>
          <Button 
            size="Medium" 
            type="Text" 
            variant="Outlined" 
            color="Secondary" 
            onClick={onExperimentClick}
          >
            실험 실행
          </Button>
          <Button 
            size="Medium" 
            type="Text" 
            variant="Contained" 
            color="Primary" 
            onClick={onCreateClick}
          >
            Ground Truth 생성
          </Button>
        </HeaderActions>
      </Header>

      <GroundTruthFilters
        filters={filters}
        onFiltersChange={onFiltersChange}
        onReset={handleResetFilters}
      />

      {groundTruths.length === 0 ? (
        <EmptyState>
          <EmptyMessage>Ground Truth가 없습니다.</EmptyMessage>
          <Button 
            size="Medium" 
            type="Text" 
            variant="Contained" 
            color="Primary" 
            onClick={onCreateClick}
          >
            첫 번째 Ground Truth 생성하기
          </Button>
        </EmptyState>
      ) : (
        <TableContainer>
          <Table>
            <thead>
              <tr>
                <Th style={{ width: '180px' }}>환자 정보</Th>
                <Th style={{ width: '150px' }}>프롬프트 타입</Th>
                <Th>예상 결과</Th>
                <Th style={{ width: '120px' }}>수정일</Th>
                <Th style={{ width: '220px' }}>작업</Th>
              </tr>
            </thead>
            <tbody>
              {groundTruths.map((gt) => (
                <GroundTruthRow
                  key={gt.id}
                  groundTruth={gt}
                  onEdit={onEditClick}
                  onDelete={onDeleteClick}
                />
              ))}
            </tbody>
          </Table>
        </TableContainer>
      )}
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
  max-width: 100%;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
`;

const Title = styled(Text)`
  font-size: 24px;
  font-weight: 600;
  color: #333;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 12px;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  text-align: center;
  background: white;
  border-radius: 8px;
`;

const EmptyMessage = styled(Text)`
  font-size: 16px;
  color: ${COLOR_SYSTEM.get("Gray")?.[600]};
  margin-bottom: 24px;
`;

const TableContainer = styled.div`
  width: 100%;
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 1000px;
`;

const Th = styled.th`
  padding: 16px;
  text-align: left;
  font-weight: 600;
  font-size: 14px;
  color: #4b5563;
  background-color: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
  white-space: nowrap;
`;

export default GroundTruthListSection;